import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Truck, Boxes, Package, Zap, ShieldCheck, Users, FileText, Calendar, Wrench,
  ClipboardList, ChevronRight, RadioTower,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card, Pill, Spinner } from "@/components/ui";
import { fmtDate } from "@/lib/dates";

// =============================================================================
// ProjectDeliverablesCard — open logistics/deliverables at a glance on the
// project Overview: plant / materials / delivery / electrical (+ integration,
// subbie, inspection). Reads project_bookings (open = not completed/cancelled),
// grouped by type. Full add/manage lives on the Schedule screen.
// =============================================================================

const META: Record<string, { label: string; icon: any; tone: string }> = {
  plant:         { label: "Plant",         icon: Truck,       tone: "text-blue-400" },
  materials:     { label: "Materials",     icon: Boxes,       tone: "text-emerald-400" },
  delivery:      { label: "Delivery",      icon: Package,     tone: "text-amber-400" },
  electrical:    { label: "Electrical",    icon: Zap,         tone: "text-yellow-400" },
  integration:   { label: "Integration",   icon: ShieldCheck, tone: "text-purple-400" },
  subcontractor: { label: "Subcontractor", icon: Users,       tone: "text-pink-400" },
  inspection:    { label: "Inspection",    icon: FileText,    tone: "text-text-muted" },
  meeting:       { label: "Meeting",       icon: Calendar,    tone: "text-text-muted" },
  outage:        { label: "Outage",        icon: RadioTower,  tone: "text-orange-400" },
  other:         { label: "Other",         icon: Wrench,      tone: "text-text-muted" },
};
const STATUS_TONE: Record<string, "open" | "pending" | "warning" | "default"> = {
  tentative: "default", confirmed: "open", in_progress: "pending", delayed: "warning",
};
const ORDER = ["plant", "materials", "delivery", "electrical", "integration", "subcontractor", "inspection", "meeting", "outage", "other"];

interface Booking {
  booking_id: string; booking_type: string; title: string;
  scheduled_start: string; status: string;
  supplier?: { legal_name: string | null } | null;
}

export default function ProjectDeliverablesCard({ projectId }: { projectId: string }) {
  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ["project_deliverables_card", projectId],
    queryFn: async () => {
      const { data } = await supabase
        .from("project_bookings")
        .select("booking_id, booking_type, title, scheduled_start, status, supplier:supplier_id ( legal_name )")
        .eq("project_id", projectId)
        .not("status", "in", "(completed,cancelled)")
        .order("scheduled_start", { ascending: true });
      // `as unknown as` and not a bare cast. The embedded supplier comes back
      // typed as a SelectQueryError by the generated types, so TS decides the
      // two shapes do not sufficiently overlap and refuses the direct cast -
      // and it started refusing it the day booking_type gained a tenth member.
      // Same two-step the rest of this codebase already uses for embeds.
      return (data ?? []) as unknown as Booking[];
    },
  });

  const groups = useMemo(() => {
    const m = new Map<string, Booking[]>();
    for (const b of bookings) {
      if (!m.has(b.booking_type)) m.set(b.booking_type, []);
      m.get(b.booking_type)!.push(b);
    }
    return ORDER.filter(t => m.has(t)).map(t => ({ type: t, items: m.get(t)! }));
  }, [bookings]);

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <ClipboardList size={14} className="text-text-muted" />
            <span className="text-[12px] uppercase tracking-wider text-text-muted">Deliverables &amp; bookings</span>
            {bookings.length > 0 && <span className="text-[11px] text-text-muted">· {bookings.length} open</span>}
          </div>
          <Link to={`/projects/${projectId}/schedule`} className="text-[12px] text-accent hover:underline inline-flex items-center gap-1">
            Manage <ChevronRight size={12} />
          </Link>
        </div>

        {isLoading && <div className="flex justify-center py-3"><Spinner size={14} /></div>}

        {!isLoading && bookings.length === 0 && (
          <div className="text-[12px] text-text-muted py-1">
            Nothing booked yet — track plant, materials, delivery, electrical and inspections here.{" "}
            <Link to={`/projects/${projectId}/schedule`} className="text-accent hover:underline">Add one →</Link>
          </div>
        )}

        {!isLoading && groups.length > 0 && (
          <div className="space-y-3">
            {groups.map(g => {
              const meta = META[g.type] ?? META.other;
              const Icon = meta.icon;
              return (
                <div key={g.type}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon size={12} className={meta.tone} />
                    <span className="text-[11px] font-medium text-text">{meta.label}</span>
                    <span className="text-[10px] text-text-muted">· {g.items.length}</span>
                  </div>
                  <div className="space-y-1">
                    {g.items.map(b => (
                      <div key={b.booking_id} className="flex items-center gap-2 text-[12px] pl-4">
                        <span className="text-text truncate flex-1 min-w-0">{b.title}</span>
                        {b.supplier?.legal_name && <span className="text-text-dim truncate max-w-[120px] hidden sm:inline">{b.supplier.legal_name}</span>}
                        <span className="text-text-muted font-mono text-[11px] flex-shrink-0">{fmtDate(b.scheduled_start)}</span>
                        <Pill label={b.status.replace(/_/g, " ")} tone={STATUS_TONE[b.status] ?? "default"} small />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
}
